import { useEffect, useRef, useState } from 'react';
import type { HubClientConfig, ModuleRole, SessionUser } from '@cortex/shared';
import { MODULE_ROLES } from '@cortex/shared';
import { Api } from '../lib/api';

/** Why the server refused a POST /hub-auth launch, in words the person can act on. */
const LAUNCH_ERRORS: Record<string, string> = {
  expired: 'That launch link has expired.',
  used: 'That launch link has already been used.',
  'wrong-module': 'That launch link was for a different module.',
  'no-access': 'You do not have access to Cortex. Ask an administrator to add it to your job role.',
  disabled: 'Your Cortex account is deactivated. Ask an administrator.',
  conflict: 'Your email is linked to a different Command Centre account in Cortex. Ask an administrator to sort it out.',
  'cross-site': 'That launch did not come from the Command Centre.',
  invalid: 'That launch link is not valid.',
};

/**
 * Command Centre launch handshake (MODULE_CONTRACT.md v3 §3). POST /hub-auth verifies the launch on
 * the server and lands here with one of:
 *  - `?handoff=1`: our session is waiting behind a one-time HttpOnly cookie; claim it.
 *  - `?error=<code>`: the launch was refused; say why.
 * The legacy `?token=<jwt>` handoff is gone: a launch token in a URL ends up in history and logs.
 */
export function HubAuthPage({ onSession }: { onSession: (token: string, user: SessionUser) => void }) {
  const [error, setError] = useState<string | null>(null);
  const exchanged = useRef(false);

  useEffect(() => {
    if (exchanged.current) return; // React StrictMode double-invoke guard
    exchanged.current = true;
    const q = new URLSearchParams(window.location.search);
    const failed = q.get('error');
    const handoff = q.get('handoff') === '1';
    // Strip anything sensitive from the URL/history immediately.
    window.history.replaceState(null, '', '/hub-auth');
    const enter = ({ token: session, user }: { token: string; user: SessionUser }) => {
      onSession(session, user);
      window.history.replaceState(null, '', '/');
    };
    if (failed) {
      setError(LAUNCH_ERRORS[failed] ?? LAUNCH_ERRORS.invalid);
      return;
    }
    if (handoff) {
      Api.hub.session().then(enter).catch((err: Error) => setError(err.message));
      return;
    }
    setError(q.has('token') ? 'This launch link is from an older Command Centre. Open Cortex again from the Command Centre.' : 'Please launch Cortex from the Command Centre.');
  }, [onSession]);

  return (
    <LoginCard>
      {error ? (
        <>
          <h2>Launch Cortex from the Command Centre</h2>
          <p className="muted">{error}</p>
        </>
      ) : (
        <p className="muted">Signing you in…</p>
      )}
    </LoginCard>
  );
}

/** No session: point at the hub, or offer the dev login when the API allows it. */
export function LaunchPage({ config, onSession }: { config: HubClientConfig | null; onSession: (token: string, user: SessionUser) => void }) {
  const [role, setRole] = useState<ModuleRole>('ADMIN');
  const [name, setName] = useState('Dev User');
  const [email, setEmail] = useState('dev@cqtex.com.au');
  const [error, setError] = useState<string | null>(null);
  const devLogin = async () => {
    try {
      const r = await Api.auth.devLogin({ role, name, email });
      onSession(r.token, r.user);
    } catch (err) {
      setError((err as Error).message);
    }
  };
  return (
    <LoginCard>
      <h2>Launch Cortex from the Command Centre</h2>
      <p className="muted">Cortex has no login of its own. Sessions come from the Command Centre launch handshake.</p>
      {config?.hubUrl && <a className="button-link" href={config.hubUrl}>Open the Command Centre →</a>}
      {config?.devLoginEnabled && (
        <div className="dev-login">
          <div className="label">Dev login (DEV_LOGIN_ENABLED, never in production)</div>
          <label>Name <input type="text" value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label>Email <input type="text" value={email} onChange={(e) => setEmail(e.target.value)} /></label>
          <label>
            Role
            <select value={role} onChange={(e) => setRole(e.target.value as ModuleRole)}>
              {MODULE_ROLES.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </label>
          {error && <div style={{ color: 'var(--down)' }}>{error}</div>}
          <button className="primary" onClick={() => void devLogin()} style={{ justifyContent: 'center' }}>Sign in as {role}</button>
        </div>
      )}
    </LoginCard>
  );
}

function LoginCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="fullpage">
      <div className="panel login-card">
        <div className="logo-row">
          <img src="/assets/cqtex-logo.png" alt="CQTex" />
        </div>
        <div className="wordmark" style={{ fontSize: 22, justifyContent: 'center' }}>Cor<span className="x">tex</span></div>
        {children}
      </div>
    </div>
  );
}
